import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { selectAutomationById } from '@/selectors/selectors';
import { AppStoreType, AutomationAutoStagedRollout, PhasedRelease } from '@/types';
import { Nullable } from '@/typescriptHelpers';
import {
    getIsAppleAppStore,
    getIsGooglePlayStore,
    getIsHuaweiAppGallery,
    isPhasedReleaseComplete,
    isPhasedReleasePaused,
    isPhasedReleaseSuperseded,
    tryDeterminePhasedReleaseDaysToPercentageSchedule,
} from '@/helpers';
import { getRolloutPercentText } from '@/helpers/displayHelpers';

interface UsePhasedReleaseStateInput {
    appId: string;
    appStoreType: AppStoreType;
    phasedRelease: Nullable<PhasedRelease> | undefined;
    autoStagedRolloutAutomationId: Nullable<string>;
    rolloutStartedAt: Nullable<string> | undefined;
}

export interface PhasedReleaseState {
    automatedStagedRolloutEnabled: boolean;
    daysToPercent: Nullable<number[]>;
    dayNumber: number;
    hasPhasedReleaseState: boolean;
    isAcceleratedRollout: boolean;
    isPhasedRollout: boolean;
    isSuperseded: boolean;
    needsAutomatedRolloutStart: boolean;
    percentText: number;
    phasedReleasePaused: boolean;
    rolloutComplete: boolean;
    shouldShowRolloutDay: boolean;
    userFraction: Nullable<number>;
}

export const usePhasedReleaseState = (input: UsePhasedReleaseStateInput): PhasedReleaseState => {
    const { appStoreType, phasedRelease, rolloutStartedAt } = input;

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, input.appId, input.autoStagedRolloutAutomationId),
    ) as Nullable<AutomationAutoStagedRollout>;

    const automatedStagedRolloutEnabled = autoStagedRolloutAutomation?.enabled === true;

    const isAppleAppStore = getIsAppleAppStore(appStoreType);
    const isGooglePlayStore = getIsGooglePlayStore(appStoreType);
    const isHuaweiAppGallery = getIsHuaweiAppGallery(appStoreType);

    const hasPhasedReleaseState = !!phasedRelease;

    const isPhasedRollout = isAppleAppStore ? hasPhasedReleaseState : automatedStagedRolloutEnabled;

    const isAndroidStagedRolloutComplete = isGooglePlayStore && !phasedRelease && !!rolloutStartedAt;
    const rolloutComplete = isPhasedReleaseComplete(phasedRelease ?? undefined) || isAndroidStagedRolloutComplete;

    const userFraction =
        isGooglePlayStore || isHuaweiAppGallery ? (phasedRelease?.userFraction ?? null)
        : isAppleAppStore ?
            phasedRelease?.isManuallyAcceleratedPhasedRollout || phasedRelease?.isRunwayAcceleratedRollout ?
                1
            :   (phasedRelease?.userFraction ?? null)
        :   null;

    const isAcceleratedRollout =
        !!phasedRelease?.isRunwayAcceleratedRollout || !!phasedRelease?.isManuallyAcceleratedPhasedRollout;

    const hasRolloutDay = !!phasedRelease && (phasedRelease.dayNumber ?? 0) > 0;

    return {
        automatedStagedRolloutEnabled,
        daysToPercent: tryDeterminePhasedReleaseDaysToPercentageSchedule(
            hasPhasedReleaseState,
            appStoreType,
            autoStagedRolloutAutomation,
        ),
        dayNumber: phasedRelease?.dayNumber ?? 0,
        hasPhasedReleaseState,
        isAcceleratedRollout,
        isPhasedRollout,
        isSuperseded: isPhasedReleaseSuperseded(phasedRelease ?? undefined),
        needsAutomatedRolloutStart:
            (isGooglePlayStore || isHuaweiAppGallery) && automatedStagedRolloutEnabled && !rolloutStartedAt,
        percentText: getRolloutPercentText(isGooglePlayStore, rolloutComplete, userFraction),
        phasedReleasePaused: isPhasedReleasePaused(phasedRelease?.state),
        rolloutComplete,
        shouldShowRolloutDay:
            (isPhasedRollout || hasRolloutDay) && (!rolloutComplete || (isAcceleratedRollout && !!phasedRelease)),
        userFraction,
    };
};
